// Catalog.ID Client — the optional "Login code" step
// ============================================================================
// registration-restructure §15.6/§15.9, M23. After the type-back gate has
// passed and the account's onboarding is marked complete, all three wizards
// (personal, organization's founding delegate, invited-delegate activation)
// offer ONE optional step: scan the Authenticator Key and turn the login code
// on.
//
// Three rules, all deliberate:
//   * it DEFAULTS TO OFF (§15.9 — the member is at a printer, not necessarily
//     at their phone), so nothing happens unless they press *Turn on*;
//   * it NEVER blocks completion — *Skip* moves on, and so does a failure;
//   * it NEVER prints. The kit already carries the Authenticator Key sheet;
//     this step exists so the member does not have to walk back to the safe to
//     enrol the phone that is in their pocket.
//
// The secret is the ceremony's, still in memory — no `reveal` round-trip.

import { createElement } from '@catalog-shell';
import { appendAuthenticatorKeyPanel } from './authenticator-key-panel';
import {
    describeAuthenticatorError, enableAuthenticator, isCompleteAuthenticatorCode,
    normalizeAuthenticatorCode,
} from '../auth/authenticator';
import { rememberUnlockFactors } from '../auth/device-unlock';
import { signStepUp } from '../auth/step-up';
import * as deviceApi from '../api/device';
import * as deviceStorage from '../state/device-storage';
import type { ChangePinRequest } from '../api/types';
import { TOTP_PERIOD_SECONDS } from '../crypto/totp';

// ── the device checkbox (device-setup-restructure §6.5) ─────────────────────
// The same key can also be this DEVICE's second unlock factor, and this step is
// the one place a member is guaranteed to be holding the phone — the wizard's
// device step runs earlier, at a printer (§1 item 7). So the choice is offered
// here, off by default, and taken with one more code.
//
// The decisions live outside the DOM so they can be pinned by tests; the
// rendering below is the thin part.

/** Whether the choice is offered at all. "No local storage" registered no
 *  device row, so there is nothing to put a factor on. */
export function offersDeviceCodeChoice(deviceToken: string | null | undefined): boolean {
    return typeof deviceToken === 'string' && deviceToken.length > 0;
}

/** The mode to send so the node KEEPS the PIN: the one the row already has. */
export function deviceAccessModeForKeepPin(pinLength: number | null): 'pin' | 'open' {
    return pinLength === null ? 'open' : 'pin';
}

/**
 * Seconds until the authenticator app rolls to its next code. Never 0 and
 * never more than one step: on a boundary the answer is a WHOLE fresh step,
 * because a zero would leave "the next code appears in 0 seconds" on screen for
 * the second after it already had.
 */
export function secondsToNextCodeStep(nowMs: number): number {
    const into = Math.floor(nowMs / 1000) % TOTP_PERIOD_SECONDS;
    return TOTP_PERIOD_SECONDS - into;
}

/**
 * The `PUT /api/device/pin` body that turns the Authenticator factor on and
 * changes nothing else.
 *
 * Three deliberate omissions, each of them a "keep": the PIN (this step holds
 * the phone, not the PIN — `accessMode` equal to the row's current mode is what
 * tells the node to keep the stored hash, §6.5), its length, and the device
 * encryption key (replacing it would orphan the copy this browser just wrote).
 */
export function buildRequireCodeOnDeviceRequest(args: {
    deviceToken: string;
    pinLength: number | null;
    code: string;
    challengeSignature: string;
}): ChangePinRequest {
    return {
        deviceToken: args.deviceToken,
        accessMode: deviceAccessModeForKeepPin(args.pinLength),
        totpRequired: true,
        code: args.code,
        challengeSignature: args.challengeSignature,
    };
}

/** The real device write. Signs a `device-manage` step-up, which the keys in
 *  memory after finalize can produce. */
async function requireCodeOnThisDevice(cid: string, deviceToken: string, code: string): Promise<void> {
    const pinLength = deviceStorage.getPinLength(cid);
    const req = buildRequireCodeOnDeviceRequest({
        deviceToken, pinLength, code,
        challengeSignature: await signStepUp('device-manage'),
    });
    await deviceApi.changePin(req);
    // The unlock screen's first paint (§6.1) — only after the node accepted it.
    rememberUnlockFactors(cid, req.accessMode, true);
}

export interface LoginCodeStepOptions {
    /** The account whose key this is — the QR's label. */
    cid: string;
    /** The 20-byte secret the NODE holds for this account (the finalize helper
     *  reported it stored). */
    secret: Uint8Array;
    /** Called once the member has turned the code on or skipped. Never called
     *  twice. */
    onDone: () => void;
    /** Swap the enable call, for tests. */
    enable?: (code: string) => Promise<void>;
    /** The device token the wizard's device step installed, or null when the
     *  member chose "No local storage" — the checkbox is hidden for the latter.
     *  Omit and this device's stored token is read. */
    deviceToken?: string | null;
    /** Swap the device write, for tests. */
    requireOnDevice?: (code: string) => Promise<void>;
}

/** Render the step into `host` (which the caller has already emptied). */
export function renderLoginCodeStep(host: HTMLElement, options: LoginCodeStepOptions): void {
    const enable = options.enable ?? ((code: string) => enableAuthenticator(code));
    const deviceToken = options.deviceToken !== undefined
        ? options.deviceToken
        : deviceStorage.getDeviceToken(options.cid);
    const requireOnDevice = options.requireOnDevice
        ?? ((code: string) => requireCodeOnThisDevice(options.cid, deviceToken!, code));
    let finished = false;
    let stopCountdown: (() => void) | null = null;
    const finish = (): void => {
        stopCountdown?.();
        if (finished) return;
        finished = true;
        options.onDone();
    };

    host.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: 'Login code (optional)',
    }));
    host.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Your kit contains an Authenticator Key sheet. Scan it — or this code — with an '
            + 'authenticator app and type the six digits it shows to require a code at every login. '
            + 'Recommended: it means a stolen password alone cannot sign in as you.',
    }));

    appendAuthenticatorKeyPanel(host, {
        cid: options.cid,
        secret: options.secret,
        // The sheet is already printed — this step never prints.
        print: false,
    });

    const field = createElement('div', { className: 'cid-auth-field' });
    const codeInput = document.createElement('input');
    codeInput.type = 'text';
    codeInput.className = 'cid-auth-input';
    codeInput.placeholder = '000000';
    codeInput.inputMode = 'numeric';
    codeInput.autocomplete = 'one-time-code';
    codeInput.maxLength = 6;
    field.appendChild(codeInput);
    host.appendChild(field);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    host.appendChild(errorEl);

    // §6.5, default OFF like the step itself. Hidden when the device step chose
    // "No local storage": there is no device row to put the factor on.
    const deviceCheck = document.createElement('input');
    deviceCheck.type = 'checkbox';
    deviceCheck.id = 'cid-login-code-device';
    deviceCheck.checked = false;
    if (offersDeviceCodeChoice(deviceToken)) {
        const deviceRow = createElement('label', { className: 'cid-auth-checkbox-row' });
        deviceRow.setAttribute('for', deviceCheck.id);
        deviceRow.appendChild(deviceCheck);
        deviceRow.appendChild(createElement('span', {
            textContent: 'Also require the code to unlock keys on this device',
        }));
        host.appendChild(deviceRow);
        host.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'Asks for one more code now, and for a code every time this browser '
                + 'unlocks your keys. You can change it later under Devices.',
        }));
    }

    const turnOnBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Turn on',
    });
    const skipBtn = createElement('button', {
        className: 'cs-btn cs-btn--default',
        textContent: 'Skip',
    });

    const turnOn = async (): Promise<void> => {
        const code = normalizeAuthenticatorCode(codeInput.value);
        if (!isCompleteAuthenticatorCode(code)) {
            errorEl.textContent = 'Enter the six digits your authenticator app is showing.';
            errorEl.classList.add('cid-pane-error--visible');
            return;
        }
        errorEl.classList.remove('cid-pane-error--visible');
        turnOnBtn.setAttribute('disabled', '');
        skipBtn.setAttribute('disabled', '');
        turnOnBtn.textContent = 'Turning on...';
        try {
            await enable(code);
            if (deviceCheck.checked && offersDeviceCodeChoice(deviceToken)) {
                renderDeviceStage();
                return;
            }
            renderEnabled();
        } catch (err) {
            // Never blocks completion: the member can try again or skip.
            turnOnBtn.removeAttribute('disabled');
            skipBtn.removeAttribute('disabled');
            turnOnBtn.textContent = 'Turn on';
            errorEl.textContent = describeAuthenticatorError(err);
            errorEl.classList.add('cid-pane-error--visible');
        }
    };

    codeInput.addEventListener('input', () => {
        const normalized = normalizeAuthenticatorCode(codeInput.value);
        if (codeInput.value !== normalized) codeInput.value = normalized;
    });
    codeInput.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') void turnOn();
    });
    turnOnBtn.addEventListener('click', () => { void turnOn(); });
    host.appendChild(turnOnBtn);

    skipBtn.addEventListener('click', () => {
        host.innerHTML = '';
        host.appendChild(createElement('h3', {
            className: 'cid-wizard-heading',
            textContent: 'Login code skipped',
        }));
        host.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'You can turn it on any time in Settings, under Authenticator — your kit '
                + 'carries the key.',
        }));
        const continueBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cid-auth-submit',
            textContent: 'Continue',
        });
        continueBtn.addEventListener('click', finish);
        host.appendChild(continueBtn);
    });
    host.appendChild(skipBtn);

    /** The gate is on; this is the end of the step. */
    function renderEnabled(extra?: string): void {
        stopCountdown?.();
        host.innerHTML = '';
        host.appendChild(createElement('h3', {
            className: 'cid-wizard-heading',
            textContent: 'Login code is on',
        }));
        host.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'From now on, signing in asks for your password and then for the six '
                + 'digits your authenticator app is showing. You can turn it off again in '
                + 'Settings, under Authenticator.',
        }));
        if (extra) host.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: extra }));
        const continueBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cid-auth-submit',
            textContent: 'Continue',
        });
        continueBtn.addEventListener('click', finish);
        host.appendChild(continueBtn);
    }

    /**
     * The second code (§6.5). The one just spent on `enable` is below the
     * account's replay floor, so this stage waits for the NEXT step and says so
     * — a member typing the same six digits again would get a refusal with no
     * explanation. Never blocks completion: "Not now" ends the step with the
     * gate on, and so does any failure the member gives up on.
     */
    function renderDeviceStage(): void {
        host.innerHTML = '';
        host.appendChild(createElement('h3', {
            className: 'cid-wizard-heading',
            textContent: 'Login code is on',
        }));
        host.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'Now type the NEXT code your app shows, and this browser will ask for a '
                + 'code every time it unlocks your keys.',
        }));

        const countdownEl = createElement('p', { className: 'cid-pane-note' });
        host.appendChild(countdownEl);

        const startStep = Math.floor(Date.now() / 1000 / TOTP_PERIOD_SECONDS);
        let rolled = false;
        const tick = (): void => {
            if (Math.floor(Date.now() / 1000 / TOTP_PERIOD_SECONDS) > startStep) rolled = true;
            if (rolled) {
                countdownEl.textContent = 'Type the code your app is showing now.';
                return;
            }
            const left = secondsToNextCodeStep(Date.now());
            countdownEl.textContent = `The next code appears in ${left} second${left === 1 ? '' : 's'}.`;
        };
        tick();
        const timer = setInterval(tick, 500);
        stopCountdown = () => { clearInterval(timer); stopCountdown = null; };

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'cid-auth-input';
        input.placeholder = '000000';
        input.inputMode = 'numeric';
        input.autocomplete = 'one-time-code';
        input.maxLength = 6;
        host.appendChild(input);

        const stageError = createElement('div', { className: 'cid-pane-error' });
        host.appendChild(stageError);

        const applyBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cid-auth-submit',
            textContent: 'Require on this device',
        });
        const laterBtn = createElement('button', {
            className: 'cs-btn cs-btn--default',
            textContent: 'Not now',
        });

        const apply = async (): Promise<void> => {
            const code = normalizeAuthenticatorCode(input.value);
            if (!isCompleteAuthenticatorCode(code)) {
                stageError.textContent = 'Enter the six digits your authenticator app is showing.';
                stageError.classList.add('cid-pane-error--visible');
                return;
            }
            stageError.classList.remove('cid-pane-error--visible');
            applyBtn.setAttribute('disabled', '');
            laterBtn.setAttribute('disabled', '');
            applyBtn.textContent = 'Saving...';
            try {
                await requireOnDevice(code);
                renderEnabled('This browser will now ask for a code as well, every time it unlocks '
                    + 'your keys. Change it any time under Devices.');
            } catch (err) {
                applyBtn.removeAttribute('disabled');
                laterBtn.removeAttribute('disabled');
                applyBtn.textContent = 'Require on this device';
                input.value = '';
                stageError.textContent = describeAuthenticatorError(err);
                stageError.classList.add('cid-pane-error--visible');
            }
        };

        input.addEventListener('input', () => {
            const normalized = normalizeAuthenticatorCode(input.value);
            if (input.value !== normalized) input.value = normalized;
            if (isCompleteAuthenticatorCode(normalized)) void apply();
        });
        input.addEventListener('keydown', (e) => {
            if ((e as KeyboardEvent).key === 'Enter') void apply();
        });
        applyBtn.addEventListener('click', () => { void apply(); });
        laterBtn.addEventListener('click', () => { renderEnabled(); });
        host.appendChild(applyBtn);
        host.appendChild(laterBtn);
        input.focus();
    }
}
